import React, { useState, useEffect } from "react";
import "./Home.css"; // Keep your custom CSS file import
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Box, // Use Box for general layout and spacing
  Grid, // Use Grid for responsive layout
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles"; // Import useTheme
import { useSelector, useDispatch } from "react-redux";
// Removed: import { makeStyles } from "@mui/styles";
import { useNavigate, useParams } from "react-router-dom";
import { getCurrentRecipe } from "../actions/recipes";
import AddDirection from "./AddDirectionModal";
import EditDirection from "./EditDirectionModal";
import EditIcon from "@mui/icons-material/Edit";
import HomeIcon from "@mui/icons-material/Home";
import AddIcon from "@mui/icons-material/Add";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import MenuIcon from "@mui/icons-material/Menu";

function Directions() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { recipeId } = useParams();

  // Modal states
  const [open, setOpen] = useState(false);
  const [openForEdit, setOpenForEdit] = useState(false);

  // State for editing a specific direction
  const [currentDirection, setCurrentDirection] = useState({});
  const [currentDirectionIndex, setCurrentDirectionIndex] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);

  const theme = useTheme(); // Access the theme for spacing
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // --- Data Fetching ---
  useEffect(() => {
    dispatch(getCurrentRecipe(recipeId));
  }, [dispatch, recipeId]);

  const directions =
    useSelector((state) => state.recipes.currentRecipe.directions) || []; // Default to an empty array

  // --- Handlers ---
  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleEditClose = () => setOpenForEdit(false);

  const handleClickEditOpen = (tile, index) => {
    setCurrentDirection(tile);
    setCurrentDirectionIndex(index);
    setOpenForEdit(true);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Standardizing the display value for the Optional field
  function changeOptionToString(value) {
    if (value === true || value === "Yes") {
      return "Yes";
    }
    return "No";
  }

  // --- Styling Objects (Replaces makeStyles) ---
  const headerContainerSx = {
    flexGrow: 1,
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    alignItems: "center",
  };

  const navButtonContainerSx = {
    display: "flex",
    gap: theme.spacing(1), // Space between buttons
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  };

  const cardSx = {
    width: "100%",
    minHeight: 120,
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.background.paper, // Use paper color
    display: "flex",
    boxShadow: theme.shadows[3], // Slightly elevated shadow
  };

  const instructionSx = {
    fontSize: "1.1rem",
    fontWeight: 500,
    lineHeight: 1.5,
    marginBottom: theme.spacing(1),
  };

  const optionalSx = {
    fontSize: "0.9rem",
    color: theme.palette.text.secondary,
  };

  return (
    <React.Fragment>
      <AppBar position="static">
        <Toolbar sx={headerContainerSx}>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            component="div"
            sx={{ fontWeight: 600 }}
          >
            Cooking Directions
          </Typography>
          <Box sx={navButtonContainerSx}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<RestaurantIcon />}
              onClick={() => navigate("/specials")}
              sx={{ color: "#fff" }}
            >
              Specials
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={handleClickOpen}
              sx={{ color: "#fff" }}
            >
              Add Direction
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<HomeIcon />}
              onClick={() => navigate("/")}
            >
              Home
            </Button>
          </Box>
          {isMobile && (
            <IconButton edge="end" color="inherit" onClick={handleMenuOpen}>
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            navigate("/specials");
            handleMenuClose();
          }}
        >
          <RestaurantIcon sx={{ mr: 1 }} />
          Specials
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClickOpen();
            handleMenuClose();
          }}
        >
          <AddIcon sx={{ mr: 1 }} />
          Add Direction
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate("/");
            handleMenuClose();
          }}
        >
          <HomeIcon sx={{ mr: 1 }} />
          Home
        </MenuItem>
      </Menu>

      <Box sx={{ padding: theme.spacing(4) }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
          Recipe Steps
        </Typography>

        <Grid container spacing={3} justifyContent="center">
          {directions.length === 0 ? (
            <Typography
              variant="subtitle1"
              color="textSecondary"
              sx={{ mt: 5 }}
            >
              No directions added yet. Use the "Add Direction" button to start.
            </Typography>
          ) : (
            directions.map((tile, index) => (
              <Grid item xs={12} md={8} key={index}>
                <Card sx={cardSx}>
                  <CardContent sx={{ flexGrow: 1, p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      sx={{ fontWeight: 700, mb: 0.5 }}
                    >
                      Step {index + 1}
                    </Typography>
                    <Typography component="div" sx={instructionSx}>
                      {tile.instruction}
                    </Typography>
                    <Typography component="div" sx={optionalSx}>
                      Optional: **{changeOptionToString(tile.optional)}**
                    </Typography>
                  </CardContent>

                  <CardActions
                    sx={{
                      borderLeft: `1px solid ${theme.palette.divider}`,
                      p: 1,
                      minWidth: "100px",
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      onClick={() => handleClickEditOpen(tile, index)}
                      size="small"
                      color="primary"
                      sx={{ fontWeight: "bold" }}
                    >
                      Edit
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </Box>

      {/* Modals */}
      <AddDirection open={open} onClose={handleClose} />
      <EditDirection
        currentDirection={currentDirection}
        open={openForEdit}
        index={currentDirectionIndex}
        onClose={handleEditClose}
      />
    </React.Fragment>
  );
}

export default Directions;
